<template>
  <b-modal
    v-model="localIsEditModal"
    centered
    hide-footer
    title="Edit Social Learning"
    size="lg"
  >
    <form @submit.prevent="submit">
      <div class="form-group">
        <div class="row">
          <div class="col-6 mt-3">
            <label>Category <span class="text-danger">*</span></label>
            <v-select
              v-model="form.category"
              :options="category"
              label="name"
              :reduce="(e) => e.id"
              placeholder="Select Category"
            />
          </div>

          <div class="col-6 mt-3">
            <label>Title <span class="text-danger">*</span></label>
            <input
              type="text"
              v-model="form.title"
              class="form-control"
              placeholder="Coaching Title"
            />
          </div>

          <div class="col-6 mt-3">
            <label>Coach / Mentor <span class="text-danger">*</span></label>
            <v-select
              v-model="form.nik"
              :options="coaches"
              label="employee_name"
              :reduce="(c) => c.employee_nik"
              placeholder="Search By Name"
            />
          </div>

          <div class="col-6 mt-3">
            <label
              >Methodology Description <span class="text-danger">*</span></label
            >
            <input
              type="text"
              v-model="form.methodology"
              class="form-control"
              placeholder="Coaching Methodology"
            />
          </div>

          <div class="col-6 mt-3">
            <label>Related Competency <span class="text-danger">*</span></label>
            <v-select
              multiple
              v-model="form.competency"
              :options="competencyData"
              label="name"
              :reduce="(e) => e.id"
              placeholder="Select Related Competency"
            />
          </div>

          <div class="col-6 mt-3">
            <label
              >Social Learning Cover <span class="text-danger">*</span></label
            >
            <div class="col-12 px-0">
              <input
                type="file"
                ref="coverFile"
                class="form-control"
                accept="image/*"
                @change="handleFileUpload"
              />
              <small class="text-muted">
                Supported formats: JPG, PNG, GIF (Max: 2MB) - Upload new image
                to replace current cover
              </small>
            </div>
            <div class="col-12 px-0 mt-2">
              <div v-if="coverPreview" class="cover-preview">
                <div class="position-relative">
                  <img
                    :src="coverPreview"
                    alt="Cover Preview"
                    class="img-fluid rounded"
                    style="max-height: 150px; object-fit: cover; width: 100%"
                  />
                  <b-badge
                    v-if="!newCoverSelected"
                    variant="info"
                    class="position-absolute"
                    style="top: 5px; left: 5px"
                  >
                    Current Cover
                  </b-badge>
                  <b-badge
                    v-else
                    variant="success"
                    class="position-absolute"
                    style="top: 5px; left: 5px"
                  >
                    New Cover
                  </b-badge>
                </div>
                <button
                  v-if="newCoverSelected"
                  type="button"
                  class="btn btn-sm btn-danger mt-2"
                  @click="removeCover"
                >
                  <i class="fas fa-trash me-1"></i>
                  Remove New Cover
                </button>
                <button
                  v-else
                  type="button"
                  class="btn btn-sm btn-warning mt-2"
                  @click="showCoverRequiredMessage"
                >
                  <i class="fas fa-info-circle me-1"></i>
                  Cover Wajib Diisi
                </button>
              </div>
              <div v-else class="cover-placeholder">
                <div
                  class="d-flex align-items-center justify-content-center bg-light rounded"
                  style="height: 150px; border: 2px dashed #dee2e6"
                >
                  <div class="text-center text-muted">
                    <i class="fas fa-image fa-2x mb-2"></i>
                    <p class="mb-0">No cover selected</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form-group">
        <button
          style="float: right"
          class="mt-4 btn btn-primary"
          type="submit"
          :disabled="isSaving"
        >
          <span v-if="!isSaving">
            {{ form.cover ? "Update with New Cover" : "Update" }}
          </span>
          <span v-else>
            <span class="spinner-border spinner-border-sm" role="status" />
            {{ form.cover ? "Updating with Cover..." : "Updating..." }}
          </span>
        </button>
      </div>
    </form>
  </b-modal>
</template>

<script>
export default {
  name: "EditCoaching",
  props: {
    isEditModal: Boolean,
    modelValue: Boolean,
    coaches: Array,
    competencyData: Array,
    category: Array,
    isSaving: Boolean,
    editData: Object,
  },
  data() {
    return {
      form: {
        title: "",
        nik: "",
        coach_name: "",
        methodology: "",
        competency: [],
        category: "",
        cover: null,
      },
      coverPreview: null,
      newCoverSelected: false,
      maxFileSize: 2 * 1024 * 1024, // 2MB in bytes
    };
  },
  computed: {
    localIsEditModal: {
      get() {
        return this.isEditModal;
      },
      set(value) {
        this.$emit("update:isEditModal", value);
        if (!value) {
          this.resetForm();
        }
      },
    },
  },
  watch: {
    isEditModal(newVal) {
      if (!newVal) {
        this.resetForm();
      } else if (newVal && this.editData) {
        this.populateForm();
      }
    },
    editData: {
      handler(newData) {
        if (newData && this.isEditModal) {
          this.populateForm();
        }
      },
      immediate: true,
    },
  },
  methods: {
    getCompetencyIds() {
      if (!this.editData) return [];

      // Check for related_competencies array (from API response)
      if (
        this.editData.related_competencies &&
        Array.isArray(this.editData.related_competencies)
      ) {
        // Extract competency_id from the API response
        // API returns: [{ id: 19, competency_id: "FINBCTRL", competency_name: "..." }]
        return this.editData.related_competencies
          .map((comp) => comp.competency_id)
          .filter((id) => id);
      }

      return [];
    },

    populateForm() {
      if (!this.editData) return;

      this.form = {
        title: this.editData.coaching_title || this.editData.title || "",
        nik: this.editData.coach_nik || this.editData.nik || "",
        coach_name: this.editData.coach_name || "",
        methodology:
          this.editData.coaching_methodology || this.editData.methodology || "",
        competency: this.getCompetencyIds(),
        category: this.editData.type || this.editData.category || "",
        cover: null,
      };

      // Set current cover preview - try multiple possible field names
      const coverImage =
        this.editData.thumbnail ||
        this.editData.cover_image ||
        this.editData.cover;
      if (coverImage) {
        this.coverPreview = coverImage;
        this.newCoverSelected = false;
      } else {
        this.coverPreview = null;
        this.newCoverSelected = false;
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        this.$swal(
          "Tipe File Tidak Valid",
          "Pilih file gambar yang valid",
          "error"
        );
        this.resetFileInput();
        return;
      }

      // Validate file size
      if (file.size > this.maxFileSize) {
        this.$swal(
          "File Terlalu Besar",
          "Ukuran file harus kurang dari 2MB",
          "error"
        );
        this.resetFileInput();
        return;
      }

      this.form.cover = file;
      this.newCoverSelected = true;

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.coverPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    removeCover() {
      // Only allow removing if a new cover was selected
      if (this.newCoverSelected) {
        this.form.cover = null;
        this.newCoverSelected = false;
        this.resetFileInput();

        // Restore original cover preview if it exists
        const originalCover =
          this.editData?.thumbnail ||
          this.editData?.cover_image ||
          this.editData?.cover;
        if (originalCover) {
          this.coverPreview = originalCover;
        } else {
          this.coverPreview = null;
        }
      }
    },

    showCoverRequiredMessage() {
      this.$swal(
        "Cover Wajib Diisi",
        "Gambar cover wajib. Unggah cover baru untuk mengganti yang sekarang.",
        "warning"
      );
    },

    resetFileInput() {
      if (this.$refs.coverFile) {
        this.$refs.coverFile.value = "";
      }
    },

    submit() {
      // Validate required fields
      if (
        !this.form.category ||
        !this.form.title ||
        !this.form.nik ||
        !this.form.methodology ||
        !this.form.competency.length
      ) {
        this.$swal(
          "Validasi Gagal",
          "Lengkapi semua kolom wajib",
          "error"
        );
        return;
      }

      // Check if cover exists (either new upload or existing)
      const hasExistingCover =
        this.editData?.thumbnail ||
        this.editData?.cover_image ||
        this.editData?.cover;
      if (!this.form.cover && !hasExistingCover) {
        this.$swal(
          "Cover Wajib Diisi",
          "Gambar cover wajib. Unggah gambar cover.",
          "error"
        );
        return;
      }

      // Ensure competency is array of strings before emitting
      let processedCompetency = this.form.competency;
      if (Array.isArray(this.form.competency)) {
        processedCompetency = this.form.competency
          .map((comp) => String(comp))
          .filter((id) => id && id.trim());
      }

      // Use coaching_id or id or fallback to editData id
      const itemId = this.editData?.coaching_id || this.editData?.id;
      this.$emit("update", {
        ...this.form,
        competency: processedCompetency,
        id: itemId,
      });
    },

    resetForm() {
      this.form = {
        title: "",
        nik: "",
        coach_name: "",
        methodology: "",
        competency: [],
        category: "",
        cover: null,
      };
      this.coverPreview = null;
      this.newCoverSelected = false;
      this.resetFileInput();
    },
  },
};
</script>

<style scoped>
.cover-preview {
  position: relative;
}

.cover-preview img {
  border: 2px solid #dee2e6;
  transition: all 0.3s ease;
}

.cover-preview img:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.cover-placeholder {
  transition: all 0.3s ease;
}

.cover-placeholder:hover {
  background-color: #f8f9fa !important;
}

.form-control:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.btn-danger {
  transition: all 0.2s ease;
}

.btn-danger:hover {
  transform: translateY(-1px);
}

.position-relative {
  position: relative;
}

.position-absolute {
  position: absolute;
}
</style>
